/**
 * Nathalie daily messaging tick — pure helper extracted from
 * coreSlice.nextDay (02/06/2026 night refactor).
 *
 * Centralises every messaging side-effect that Nathalie (the assistant
 * support character) emits on a regular cadence :
 *
 *   • Discrete board preparation memo (5 days before mid-year + annual)
 *   • Weekly contract recap (every Monday, i.e. totalDays % 7 === 0)
 *   • Monthly evolution advice (28th of every month)
 *   • Monthly AO recap, signed Marc / Sylvie / Léa (28th of every month)
 *
 * The builders themselves are already pure ; this helper just collects
 * their outputs into a flat array of messages so the orchestrator pushes
 * them in one pass.
 *
 * Returns an array of message payloads to push via `pushCustomMessage`.
 * Pure : no `set()`, no `get()`.
 */

import { buildNathalieBoardMemo } from './nathalieBoardMemo';
import { buildNathalieWeeklyRecap } from './nathalieWeeklyRecap';
import { buildNathalieMonthlyAdvice } from './nathalieMonthlyAdvice';
import { buildMonthlyTenderRecap } from './tenderReportBuilder';

/**
 * @param {Object} state - pre-flip game state
 * @returns {Array<MessagePayload>}
 */
export function buildNathalieDailyMessages(state) {
  const out = [];

  // ── Discrete prep memo : 5 days BEFORE each board ──
  if (state.day === 25 && (state.month === 6 || state.month === 12)) {
    const memo = buildNathalieBoardMemo(state, state.month === 6 ? 'midyear' : 'annual');
    if (memo) out.push(memo);
  }

  // ── Weekly contract recap : every Monday (totalDays % 7 === 0) ──
  if (((state.totalDays || 0) % 7) === 0 && (state.totalDays || 0) > 0) {
    const recap = buildNathalieWeeklyRecap(state);
    if (recap) out.push(recap);
  }

  // ── Monthly evolution advice : 28th of every month ──
  if (state.day === 28) {
    const advice = buildNathalieMonthlyAdvice(state);
    if (advice) out.push(advice);
    // ── 02/06/2026 — Monthly AO recap signed Marc / Sylvie / Léa ──
    const recap = buildMonthlyTenderRecap(state);
    if (recap) out.push(recap);
  }

  return out;
}
